import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "../lib/api";
import useSeo from "../hooks/useSeo";
import { categorySlug } from "../lib/blogCategories";
import { Footer } from "../components/Footer";
import { ShareButtons } from "../components/ShareButtons";
import { SubscribeBar } from "../components/SubscribeBar";
import { Clock, ArrowLeft, Loader2, Calendar, User } from "lucide-react";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};
const abs = (u) => (u && u.startsWith("/") ? `${window.location.origin}${u}` : u);

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    (async () => {
      try {
        const res = await http.get(`/posts/${slug}`);
        setPost(res.data);
        setState("ok");
      } catch (e) { setState("notfound"); }
    })();
    window.scrollTo(0, 0);
  }, [slug]);

  useSeo(post ? {
    title: `${post.meta_title || post.title} — Andres Campos`,
    description: post.meta_description || post.excerpt,
    image: abs(post.cover_image),
    url: typeof window !== "undefined" ? window.location.href : undefined,
    type: "article",
  } : { title: "Blog — Andres Campos" });

  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-[#EFEBDE]"><Loader2 size={30} className="animate-spin text-[#2C5F3A]" /></div>;
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#EFEBDE] px-6 text-center">
        <h1 className="font-display font-semibold text-4xl tracking-tight text-[#16281C]">Post not found</h1>
        <Link to="/blog" className="mt-8 inline-flex items-center gap-2 bg-[#2C5F3A] text-[#EFEBDE] font-mono text-xs tracking-[0.2em] uppercase px-7 py-4 hover:bg-[#367A49] transition-colors">
          <ArrowLeft size={15} /> Back to blog
        </Link>
      </div>
    );
  }

  const related = post.related || [];

  return (
    <>
      <main className="min-h-screen bg-[#EFEBDE] pt-32 pb-24">
        <article className="max-w-[720px] mx-auto px-6">
          <Link to="/blog" data-testid="post-back" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#66766A] hover:text-[#2C5F3A] transition-colors">
            <ArrowLeft size={14} /> All posts
          </Link>

          {post.category && (
            <div className="mt-8">
              <Link
                to={`/blog/category/${categorySlug(post.category)}`}
                data-testid="post-category-link"
                className="inline-block font-mono text-[10px] tracking-[0.2em] uppercase text-[#2C5F3A] border border-[#2C5F3A]/30 px-3 py-1.5 hover:bg-[#2C5F3A] hover:text-[#EFEBDE] transition-colors"
              >
                {post.category}
              </Link>
            </div>
          )}

          <h1 data-testid="post-title" className="mt-6 font-display font-semibold text-4xl md:text-5xl tracking-tighter leading-[1.02] text-[#16281C]">
            {post.title}
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] tracking-[0.15em] uppercase text-[#8B978C]">
            <span className="inline-flex items-center gap-2"><User size={13} /> {post.author || "Andres Campos"}</span>
            <span className="inline-flex items-center gap-2"><Calendar size={13} /> {fmtDate(post.published_at)}</span>
            <span className="inline-flex items-center gap-2"><Clock size={13} /> {post.read_time} min read</span>
          </div>
        </article>

        {post.cover_image && (
          <div className="max-w-[980px] mx-auto px-6 mt-10">
            <div className="aspect-[16/9] overflow-hidden bg-[#DDE4D5] border border-[#16281C]/12">
              <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </div>
        )}

        <article className="max-w-[720px] mx-auto px-6">
          <div data-testid="post-content" className="blog-content mt-12 font-body text-[#2A3A2E]" dangerouslySetInnerHTML={{ __html: post.content }} />

          <div className="mt-12 pt-8 border-t border-[#16281C]/12">
            <ShareButtons title={post.title} />
          </div>

          <div className="mt-12">
            <SubscribeBar />
          </div>
        </article>

        {related.length > 0 && (
          <section className="max-w-[1100px] mx-auto px-6 mt-20">
            <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-[#16281C]">Related posts</h2>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid="related-post" className="group flex flex-col h-full bg-[#FAF8F1] border border-[#16281C]/12 hover:border-[#2C5F3A]/40 transition-colors overflow-hidden">
                  <div className="aspect-[16/10] overflow-hidden bg-[#DDE4D5]">
                    {p.cover_image ? <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <div className="w-full h-full bg-gradient-to-br from-[#2C5F3A] to-[#16281C]" />}
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-semibold text-lg tracking-tight text-[#16281C] leading-tight group-hover:text-[#2C5F3A] transition-colors">{p.title}</h3>
                    <div className="mt-4 font-mono text-[10px] tracking-[0.15em] uppercase text-[#8B978C] inline-flex items-center gap-1.5"><Clock size={11} /> {p.read_time} min</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}
